import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Lock, Zap, Loader2, MessageSquarePlus, X, Crosshair } from "lucide-react";

export default function SellHighBlueprint({ playerId, playerName }) {
  const { access } = useAuth();
  const isPro = !!access?.has_access;
  const nav = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [lid, setLid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pitch, setPitch] = useState(null);

  useEffect(() => {
    if (!isPro) return;
    api.get("/leagues").then(r => {
      const ls = r.data.leagues || [];
      setLeagues(ls);
      if (ls[0]) setLid(ls[0].league_id);
    });
  }, [isPro]);

  useEffect(() => {
    if (!lid) return;
    setLoading(true);
    setData(null);
    api.get(`/leagues/${lid}/sell-high/${playerId}`).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [lid, playerId]);

  const draftPitch = async (t) => {
    setPitch({ manager: t.manager, content: "", loading: true });
    try {
      const r = await api.post(`/leagues/${lid}/sell-high/${playerId}/pitch/${t.roster_id}`, {});
      setPitch({ manager: r.data.manager, content: r.data.pitch, loading: false });
    } catch (e) {
      setPitch({ manager: t.manager, content: "⚠️ " + (e?.response?.data?.detail || "Could not draft a pitch right now."), loading: false });
    }
  };

  const header = (
    <div className="flex items-center gap-2 text-xs tracking-widest font-mono mb-3" style={{ color: "#F59E0B" }}>
      <Crosshair className="w-3.5 h-3.5" /> AI SELL-HIGH BLUEPRINT
      <span className="text-[8px] tracking-widest text-[var(--accent)] border border-[var(--accent)]/40 rounded-sm px-1 py-0.5">PRO</span>
    </div>
  );

  if (!isPro) {
    return (
      <section className="card-bordered p-6 relative overflow-hidden" data-testid="sellhigh-locked">
        {header}
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[var(--accent)]/15 border border-[var(--accent)]/40 flex items-center justify-center shrink-0">
            <Lock className="w-4 h-4 text-[var(--accent)]" />
          </div>
          <div>
            <div className="font-display text-lg font-bold">Cash in on the hype</div>
            <p className="text-sm text-[var(--text-2)] mt-1 max-w-xl">
              Pro reveals exactly <span className="text-white">which managers in your league</span> are most likely to overpay for {playerName} — ranked by their real trade tendencies & current roster needs — plus a ready-to-send AI pitch tuned to each one.
            </p>
            <button onClick={() => nav("/pricing")} className="btn-primary text-sm mt-4 inline-flex items-center gap-2" data-testid="sellhigh-upgrade-btn">
              <Zap className="w-4 h-4" /> Unlock the Blueprint
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="card-bordered p-6" data-testid="sellhigh-blueprint">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {header}
        {leagues.length > 0 && (
          <select className="input-dark md:w-64 text-sm" value={lid} onChange={e => setLid(e.target.value)} data-testid="sellhigh-league-select">
            {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
          </select>
        )}
      </div>
      <p className="text-sm text-[var(--text-2)] mb-4">Leaguemates most likely to overpay for {playerName}, ranked on real trade tendencies + live roster needs.</p>

      {loading && <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>}

      {!loading && data?.note && (data.targets || []).length === 0 && (
        <div className="card-bordered p-5 text-sm text-[var(--text-3)]" data-testid="sellhigh-note">{data.note}</div>
      )}

      {!loading && data && (data.targets || []).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.targets.map((t, i) => (
            <div key={t.roster_id} className="card-bordered p-4" data-testid={`sellhigh-target-${t.roster_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold">{t.manager}</div>
                <div className="font-mono text-[10px] text-[var(--accent)] border border-[var(--accent)]/30 rounded-sm px-1.5 py-0.5">#{i + 1} TARGET</div>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {(t.tags || []).slice(0, 3).map((tag, j) => (
                  <span key={j} className="text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-[var(--text-2)] bg-black/40">{tag}</span>
                ))}
              </div>
              <ul className="space-y-1 mb-3">
                {(t.reasons || []).map((r, j) => (
                  <li key={j} className="text-xs text-[var(--text-2)] flex items-start gap-1.5">
                    <span className="text-[var(--accent)] mt-0.5">›</span>{r}
                  </li>
                ))}
              </ul>
              <button onClick={() => draftPitch(t)} className="btn-primary w-full text-xs inline-flex items-center justify-center gap-2" data-testid={`sellhigh-pitch-${t.roster_id}`}>
                <MessageSquarePlus className="w-3.5 h-3.5" /> Draft AI pitch
              </button>
            </div>
          ))}
        </div>
      )}

      {pitch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="sellhigh-pitch-modal" onClick={() => setPitch(null)}>
          <div className="card-bordered w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 relative" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPitch(null)} className="absolute top-4 right-4 text-[var(--text-3)] hover:text-white" data-testid="sellhigh-pitch-close"><X className="w-5 h-5" /></button>
            <div className="text-xs tracking-widest font-mono mb-1" style={{ color: "#F59E0B" }}>// SELL-HIGH PITCH</div>
            <div className="font-display text-2xl font-black uppercase tracking-tight mb-4">Target: {pitch.manager}</div>
            {pitch.loading ? (
              <div className="py-10 flex flex-col items-center gap-3 text-[var(--text-3)]">
                <Loader2 className="w-6 h-6 spin text-[var(--accent)]" />
                <span className="text-sm">Drafting a pitch tuned to their tendencies…</span>
              </div>
            ) : (
              <div className="md-body text-sm"><ReactMarkdown remarkPlugins={[remarkGfm]}>{pitch.content}</ReactMarkdown></div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
